<template>
    <div class="apply-for-trial-form en" :class="formType">
        <!-- <div>
            <div id="hubspotForm" />
        </div> -->
        <AForm
            ref="applyForm"
            :model="formState.formInline"
            name="apply-for-trial"
            :rules="rules"
            :validate-messages="validateMessages"
            :layout="formType === 'mobile' ? 'vertical' : 'horizontal'"
            :label-col="formState.labelCol"
            :wrapper-col="formState.wrapperCol"
        >
            <div class="scroll-y-container">
                <AFormItem
                    v-for="formItem in filteredFormItems"
                    :key="formItem.key"
                    :name="formItem.key"
                    :label="formItem.label"
                    :class="formItem.key"
                >
                    <AInput
                        v-if="formItem.inputType === 'text'"
                        v-model:value="formState.formInline[formItem.key]"
                        :placeholder="formItem.tPlaceholder"
                        :type="'text'"
                        :maxlength="99"
                        autocomplete="off"
                    />
                    <ACheckboxGroup
                        v-if="formItem.inputType === 'checkboxGroup'"
                        v-model:value="formState.formInline[formItem.key]"
                        class="custom-checkbox-group"
                    >
                        <ACheckbox
                            v-for="option in formItem.options"
                            :key="option.label"
                            name="type"
                            :value="option.value"
                        >
                            {{ option.label }}
                        </ACheckbox>
                    </ACheckboxGroup>
                    <ASelect
                        v-else-if="formItem.inputType === 'selector'"
                        v-model:value="formState.formInline[formItem.key]"
                        :placeholder="formItem.tPlaceholder"
                        :get-popup-container="(triggerNode) => triggerNode.parentNode"
                        :dropdown-match-select-width="true"
                    >
                        <ASelectOption
                            v-for="companyType in formItem.options"
                            :key="companyType.label"
                            :value="companyType.value"
                        >
                            <ATooltip placement="topLeft">
                                <template #title>
                                    <span> {{ companyType.label }}</span>
                                </template>
                                <span> {{ companyType.label }}</span>
                            </ATooltip>
                        </ASelectOption>
                    </ASelect>

                    <ATextarea
                        v-if="formItem.inputType === 'textarea'"
                        v-model:value="formState.formInline[formItem.key]"
                        :placeholder="formItem.tPlaceholder"
                        :maxlength="99"
                        autocomplete="off"
                        auto-size
                    />
                </AFormItem>
                <AFormItem name="policyConfirm" class="policy-confirm" :wrapper-col="{ span: 24 }">
                    <ACheckbox v-model:checked="formState.policyConfirm">
                        <div class="policy-confirm-checkbox-text">
                            <span>{{ $t('global_home_header_form_conform_text') }}</span>
                            <CommonComponentsLink
                                :link-text="'global_home_header_form_conform_policy'"
                                @click.prevent="openPolicyFile('privacy')"
                            />
                        </div>
                    </ACheckbox>
                </AFormItem>
            </div>
            <div class="fixed-bottom-container">
                <AFormItem class="apply-for-trial-form-submit-btn" :wrapper-col="{ span: 24 }">
                    <CommonComponentsButtonLine
                        ref="submitBtn"
                        transparent
                        light
                        :button-text="t('global_link_button_1')"
                        :disabled="!formState.isFinished"
                        @click="onSubmit"
                    />
                </AFormItem>
            </div>
        </AForm>
    </div>
</template>

<script lang="ts" setup>
    import { useRouter } from 'vue-router';
    import { ref, onMounted, watch, onBeforeMount, reactive, computed, Ref } from 'vue';
    import type { Rule, FormInstance } from 'ant-design-vue/es/form';
    import { gsap } from 'gsap';
    import { ScrollTrigger, Observer } from 'gsap/all';
    import { postHubspotForm } from '~~/composables/useHubspot.ts';
    import { openPolicyFile } from '~~/composables/usePolicyFile.ts';
    import i18n from '@/locales';
    import message from '~~/antd//message/adapter';

    gsap.registerPlugin(ScrollTrigger);
    const { t } = i18n.global;
    const router = useRouter();

    type hsFormKey =
        | 'membership_type'
        | 'phone'
        | 'website'
        | 'company_size'
        | 'market_segment'
        | 'job_title'
        | 'form_source'
        | 'firstname'
        | 'lastname'
        | 'email'
        | 'country__region'
        | 'other_message'
        | 'products_interested_in';

    const props = withDefaults(
        defineProps<{
            formType?: 'dialog' | 'mobile';
        }>(),
        { formType: 'dialog' },
    );

    const submitBtn = ref<HTMLButtonElement | null>(null);
    const isSubmitting = ref<boolean>(false);

    const emits = defineEmits<{
        (e: 'update:modelValue', value: boolean): void;
    }>();

    watch(
        isSubmitting,
        (value) => {
            emits('update:modelValue', value);
        },
        { immediate: true },
    );

    // ant-vue-design 校验信息模版，https://github.com/vueComponent/ant-design-vue/blob/main/components/form/utils/messages.ts
    /* eslint-disable  */
    const validateMessages = reactive({
        // required: () => t('global_validate_message_0') + '${label}!',
        required: (key) => {
            if (formState.formConfig.filter((item) => item.key === key)[0].inputType === 'selector')
                return 'Please select an option from the dropdown menu.';
            else return 'Please complete this required field.';
        },
    });

    const validatePhone = async (_rule: Rule, value: string) => {
        if (value === '') {
            return Promise.reject('Please complete this required field.');
        } else if (/^\+\d{1,4} \d+$/.test(value)) {
            return Promise.resolve();
        } else {
            return Promise.reject(t('global_validate_message_2'));
        }
    };

    const validateEmail = async (_rule: Rule, value: string) => {
        const re =
            /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        if (value === '') {
            return Promise.reject('Please complete this required field.');
        } else if (!re.test(String(value).toLowerCase())) {
            return Promise.reject(t('global_validate_message_4'));
        } else {
            return Promise.resolve();
        }
    };

    const validateWebsite = async (_rule: Rule, value: string) => {};

    // hubspot 表单数据 https://share-eu1.hsforms.com/1Kt_c5KwOQ-GPXApHtjuW9Af6b0c
    // 取自接口 https://forms-eu1.hsforms.com/embed/v3/form/25488444/2adfdce4-ac0e-43e1-8f5c-0a47b63b96f4/json?hs_static_app=forms-embed&hs_static_app_version=1.3812&X-HubSpot-Static-App-Info=forms-embed-1.3812&hutk=cedb96601dea875d9ca5716891dcd836
    const createData = {
        userTypeList: [
            {
                label: 'Individual Users',
                value: 'Individual Users',
            },
            {
                label: 'Business Users',
                value: 'Business Users',
            },
        ],
        companySizeList: [
            {
                label: 'Just me',
                value: 'Just me',
            },
            {
                label: '1-5 employees',
                value: '1-5 employees',
            },
            {
                label: '5-20 employees',
                value: '5-20 employees',
            },
            {
                label: '20-100 employees',
                value: '20-100 employees',
            },
            {
                label: '100-500 employees',
                value: '100-500 employees',
            },
            {
                label: '500-1000 employees',
                value: '500-1000 employees',
            },
            {
                label: '1000-5000 employees',
                value: '1000-5000 employees',
            },
            {
                label: '5000+ employees',
                value: '5000+ employees',
            },
        ],
        marketSegmentList: [
            {
                label: 'Brand',
                value: 'Brand',
            },
            {
                label: 'Textile',
                value: 'Textile',
            },
            {
                label: 'Retail',
                value: 'Retail',
            },
            {
                label: 'Design',
                value: 'Design',
            },
            {
                label: 'Manufacturing',
                value: 'Manufacturing',
            },
            {
                label: 'Education',
                value: 'Education',
            },
            {
                label: 'Service',
                value: 'Service',
            },
            {
                label: 'Technology',
                value: 'Technology',
            },
            {
                label: 'other',
                value: 'other',
            },
        ],
        formSourceList: [
            {
                label: 'Request a Demo',
                value: 'Request a Demo',
            },
            {
                label: 'Apply a Free Trial',
                value: 'Apply a Free Trial',
            },
            {
                label: 'Need Technical Support',
                value: 'Need Technical Support',
            },
            {
                label: 'Learn more about Style3D Partner Program',
                value: 'Learn more about Style3D Partner Program',
            },
            {
                label: 'Subscribe to Style3D Newsletter',
                value: 'Subscribe to Style3D Newsletter',
            },
            {
                label: 'Having problems Downloading the Trail Software',
                value: 'Having problems Downloading the Trail Software',
            },
            {
                label: 'Other',
                value: 'Other',
            },
        ],
        countryRegionList: [
            {
                label: 'Afghanistan',
                value: 'Afghanistan',
                displayOrder: 1,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Albania',
                value: 'Albania',
                displayOrder: 2,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Algeria',
                value: 'Algeria',
                displayOrder: 3,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'American Samoa',
                value: 'American Samoa',
                displayOrder: 4,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Andorra',
                value: 'Andorra',
                displayOrder: 5,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Angola',
                value: 'Angola',
                displayOrder: 6,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Anguilla',
                value: 'Anguilla',
                displayOrder: 7,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Antarctica',
                value: 'Antarctica',
                displayOrder: 8,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Antigua and Barbuda',
                value: 'Antigua and Barbuda',
                displayOrder: 9,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Argentina',
                value: 'Argentina',
                displayOrder: 10,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Armenia',
                value: 'Armenia',
                displayOrder: 11,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Aruba',
                value: 'Aruba',
                displayOrder: 12,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Asia/Pacific Region',
                value: 'Asia/Pacific Region',
                displayOrder: 13,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Australia',
                value: 'Australia',
                displayOrder: 14,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Austria',
                value: 'Austria',
                displayOrder: 15,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Azerbaijan',
                value: 'Azerbaijan',
                displayOrder: 16,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Bahamas',
                value: 'Bahamas',
                displayOrder: 17,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Bahrain',
                value: 'Bahrain',
                displayOrder: 18,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Bangladesh',
                value: 'Bangladesh',
                displayOrder: 19,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Barbados',
                value: 'Barbados',
                displayOrder: 20,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Belarus',
                value: 'Belarus',
                displayOrder: 21,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Belgium',
                value: 'Belgium',
                displayOrder: 22,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Belize',
                value: 'Belize',
                displayOrder: 23,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Benin',
                value: 'Benin',
                displayOrder: 24,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Bermuda',
                value: 'Bermuda',
                displayOrder: 25,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Bhutan',
                value: 'Bhutan',
                displayOrder: 26,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Bolivia',
                value: 'Bolivia',
                displayOrder: 27,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Bosnia and Herzegovina',
                value: 'Bosnia and Herzegovina',
                displayOrder: 28,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Botswana',
                value: 'Botswana',
                displayOrder: 29,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Bouvet Island',
                value: 'Bouvet Island',
                displayOrder: 30,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Brazil',
                value: 'Brazil',
                displayOrder: 31,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'British Indian Ocean Territory',
                value: 'British Indian Ocean Territory',
                displayOrder: 32,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'British Virgin Islands',
                value: 'British Virgin Islands',
                displayOrder: 33,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Brunei',
                value: 'Brunei',
                displayOrder: 34,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Bulgaria',
                value: 'Bulgaria',
                displayOrder: 35,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Burkina Faso',
                value: 'Burkina Faso',
                displayOrder: 36,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Burundi',
                value: 'Burundi',
                displayOrder: 37,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Cambodia',
                value: 'Cambodia',
                displayOrder: 38,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Cameroon',
                value: 'Cameroon',
                displayOrder: 39,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Canada',
                value: 'Canada',
                displayOrder: 40,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Cape Verde',
                value: 'Cape Verde',
                displayOrder: 41,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Caribbean Netherlands',
                value: 'Caribbean Netherlands',
                displayOrder: 42,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Cayman Islands',
                value: 'Cayman Islands',
                displayOrder: 43,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Central African Republic',
                value: 'Central African Republic',
                displayOrder: 44,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Chad',
                value: 'Chad',
                displayOrder: 45,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Chile',
                value: 'Chile',
                displayOrder: 46,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'China',
                value: 'China',
                displayOrder: 47,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Christmas Island',
                value: 'Christmas Island',
                displayOrder: 48,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Cocos (Keeling) Islands',
                value: 'Cocos (Keeling) Islands',
                displayOrder: 49,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Colombia',
                value: 'Colombia',
                displayOrder: 50,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Comoros',
                value: 'Comoros',
                displayOrder: 51,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Congo',
                value: 'Congo',
                displayOrder: 52,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Cook Islands',
                value: 'Cook Islands',
                displayOrder: 53,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Costa Rica',
                value: 'Costa Rica',
                displayOrder: 54,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: "Cote d'Ivoire",
                value: "Cote d'Ivoire",
                displayOrder: 55,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Croatia',
                value: 'Croatia',
                displayOrder: 56,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Cuba',
                value: 'Cuba',
                displayOrder: 57,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Cura\u00E7ao',
                value: 'Cura\u00E7ao',
                displayOrder: 58,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Cyprus',
                value: 'Cyprus',
                displayOrder: 59,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Czech Republic',
                value: 'Czech Republic',
                displayOrder: 60,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Democratic Republic of the Congo',
                value: 'Democratic Republic of the Congo',
                displayOrder: 61,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Denmark',
                value: 'Denmark',
                displayOrder: 62,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Djibouti',
                value: 'Djibouti',
                displayOrder: 63,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Dominica',
                value: 'Dominica',
                displayOrder: 64,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Dominican Republic',
                value: 'Dominican Republic',
                displayOrder: 65,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'East Timor',
                value: 'East Timor',
                displayOrder: 66,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Ecuador',
                value: 'Ecuador',
                displayOrder: 67,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Egypt',
                value: 'Egypt',
                displayOrder: 68,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'El Salvador',
                value: 'El Salvador',
                displayOrder: 69,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Equatorial Guinea',
                value: 'Equatorial Guinea',
                displayOrder: 70,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Eritrea',
                value: 'Eritrea',
                displayOrder: 71,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Estonia',
                value: 'Estonia',
                displayOrder: 72,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Ethiopia',
                value: 'Ethiopia',
                displayOrder: 73,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Europe',
                value: 'Europe',
                displayOrder: 74,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Falkland Islands',
                value: 'Falkland Islands',
                displayOrder: 75,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Faroe Islands',
                value: 'Faroe Islands',
                displayOrder: 76,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Fiji',
                value: 'Fiji',
                displayOrder: 77,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Finland',
                value: 'Finland',
                displayOrder: 78,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'France',
                value: 'France',
                displayOrder: 79,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'French Guiana',
                value: 'French Guiana',
                displayOrder: 80,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'French Polynesia',
                value: 'French Polynesia',
                displayOrder: 81,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'French Southern and Antarctic Lands',
                value: 'French Southern and Antarctic Lands',
                displayOrder: 82,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Gabon',
                value: 'Gabon',
                displayOrder: 83,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Gambia',
                value: 'Gambia',
                displayOrder: 84,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Georgia',
                value: 'Georgia',
                displayOrder: 85,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Germany',
                value: 'Germany',
                displayOrder: 86,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Ghana',
                value: 'Ghana',
                displayOrder: 87,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Gibraltar',
                value: 'Gibraltar',
                displayOrder: 88,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Greece',
                value: 'Greece',
                displayOrder: 89,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Greenland',
                value: 'Greenland',
                displayOrder: 90,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Grenada',
                value: 'Grenada',
                displayOrder: 91,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Guadeloupe',
                value: 'Guadeloupe',
                displayOrder: 92,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Guam',
                value: 'Guam',
                displayOrder: 93,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Guatemala',
                value: 'Guatemala',
                displayOrder: 94,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Guernsey',
                value: 'Guernsey',
                displayOrder: 95,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Guinea',
                value: 'Guinea',
                displayOrder: 96,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Guinea-Bissau',
                value: 'Guinea-Bissau',
                displayOrder: 97,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Guyana',
                value: 'Guyana',
                displayOrder: 98,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Haiti',
                value: 'Haiti',
                displayOrder: 99,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Heard Island and McDonald Islands',
                value: 'Heard Island and McDonald Islands',
                displayOrder: 100,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Honduras',
                value: 'Honduras',
                displayOrder: 101,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Hong Kong',
                value: 'Hong Kong',
                displayOrder: 102,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Hungary',
                value: 'Hungary',
                displayOrder: 103,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Iceland',
                value: 'Iceland',
                displayOrder: 104,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'India',
                value: 'India',
                displayOrder: 105,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Indonesia',
                value: 'Indonesia',
                displayOrder: 106,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Iran',
                value: 'Iran',
                displayOrder: 107,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Iraq',
                value: 'Iraq',
                displayOrder: 108,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Ireland',
                value: 'Ireland',
                displayOrder: 109,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Isle of Man',
                value: 'Isle of Man',
                displayOrder: 110,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Israel',
                value: 'Israel',
                displayOrder: 111,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Italy',
                value: 'Italy',
                displayOrder: 112,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Jamaica',
                value: 'Jamaica',
                displayOrder: 113,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Japan',
                value: 'Japan',
                displayOrder: 114,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Jersey',
                value: 'Jersey',
                displayOrder: 115,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Jordan',
                value: 'Jordan',
                displayOrder: 116,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Kazakhstan',
                value: 'Kazakhstan',
                displayOrder: 117,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Kenya',
                value: 'Kenya',
                displayOrder: 118,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Kiribati',
                value: 'Kiribati',
                displayOrder: 119,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Kosovo',
                value: 'Kosovo',
                displayOrder: 120,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Kuwait',
                value: 'Kuwait',
                displayOrder: 121,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Kyrgyzstan',
                value: 'Kyrgyzstan',
                displayOrder: 122,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Laos',
                value: 'Laos',
                displayOrder: 123,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Latvia',
                value: 'Latvia',
                displayOrder: 124,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Lebanon',
                value: 'Lebanon',
                displayOrder: 125,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Lesotho',
                value: 'Lesotho',
                displayOrder: 126,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Liberia',
                value: 'Liberia',
                displayOrder: 127,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Libya',
                value: 'Libya',
                displayOrder: 128,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Liechtenstein',
                value: 'Liechtenstein',
                displayOrder: 129,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Lithuania',
                value: 'Lithuania',
                displayOrder: 130,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Luxembourg',
                value: 'Luxembourg',
                displayOrder: 131,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Macau',
                value: 'Macau',
                displayOrder: 132,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Macedonia (FYROM)',
                value: 'Macedonia (FYROM)',
                displayOrder: 133,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Madagascar',
                value: 'Madagascar',
                displayOrder: 134,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Malawi',
                value: 'Malawi',
                displayOrder: 135,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Malaysia',
                value: 'Malaysia',
                displayOrder: 136,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Maldives',
                value: 'Maldives',
                displayOrder: 137,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Mali',
                value: 'Mali',
                displayOrder: 138,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Malta',
                value: 'Malta',
                displayOrder: 139,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Marshall Islands',
                value: 'Marshall Islands',
                displayOrder: 140,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Martinique',
                value: 'Martinique',
                displayOrder: 141,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Mauritania',
                value: 'Mauritania',
                displayOrder: 142,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Mauritius',
                value: 'Mauritius',
                displayOrder: 143,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Mayotte',
                value: 'Mayotte',
                displayOrder: 144,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Mexico',
                value: 'Mexico',
                displayOrder: 145,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Micronesia',
                value: 'Micronesia',
                displayOrder: 146,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Moldova',
                value: 'Moldova',
                displayOrder: 147,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Monaco',
                value: 'Monaco',
                displayOrder: 148,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Mongolia',
                value: 'Mongolia',
                displayOrder: 149,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Montenegro',
                value: 'Montenegro',
                displayOrder: 150,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Montserrat',
                value: 'Montserrat',
                displayOrder: 151,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Morocco',
                value: 'Morocco',
                displayOrder: 152,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Mozambique',
                value: 'Mozambique',
                displayOrder: 153,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Myanmar (Burma)',
                value: 'Myanmar (Burma)',
                displayOrder: 154,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Namibia',
                value: 'Namibia',
                displayOrder: 155,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Nauru',
                value: 'Nauru',
                displayOrder: 156,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Nepal',
                value: 'Nepal',
                displayOrder: 157,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Netherlands',
                value: 'Netherlands',
                displayOrder: 158,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Netherlands Antilles',
                value: 'Netherlands Antilles',
                displayOrder: 159,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'New Caledonia',
                value: 'New Caledonia',
                displayOrder: 160,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'New Zealand',
                value: 'New Zealand',
                displayOrder: 161,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Nicaragua',
                value: 'Nicaragua',
                displayOrder: 162,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Niger',
                value: 'Niger',
                displayOrder: 163,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Nigeria',
                value: 'Nigeria',
                displayOrder: 164,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Niue',
                value: 'Niue',
                displayOrder: 165,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Norfolk Island',
                value: 'Norfolk Island',
                displayOrder: 166,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'North Korea',
                value: 'North Korea',
                displayOrder: 167,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Northern Mariana Islands',
                value: 'Northern Mariana Islands',
                displayOrder: 168,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Norway',
                value: 'Norway',
                displayOrder: 169,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Oman',
                value: 'Oman',
                displayOrder: 170,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Pakistan',
                value: 'Pakistan',
                displayOrder: 171,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Palau',
                value: 'Palau',
                displayOrder: 172,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Palestine',
                value: 'Palestine',
                displayOrder: 173,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Panama',
                value: 'Panama',
                displayOrder: 174,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Papua New Guinea',
                value: 'Papua New Guinea',
                displayOrder: 175,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Paraguay',
                value: 'Paraguay',
                displayOrder: 176,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Peru',
                value: 'Peru',
                displayOrder: 177,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Philippines',
                value: 'Philippines',
                displayOrder: 178,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Pitcairn Islands',
                value: 'Pitcairn Islands',
                displayOrder: 179,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Poland',
                value: 'Poland',
                displayOrder: 180,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Portugal',
                value: 'Portugal',
                displayOrder: 181,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Puerto Rico',
                value: 'Puerto Rico',
                displayOrder: 182,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Qatar',
                value: 'Qatar',
                displayOrder: 183,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Romania',
                value: 'Romania',
                displayOrder: 184,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Russia',
                value: 'Russia',
                displayOrder: 185,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Rwanda',
                value: 'Rwanda',
                displayOrder: 186,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'R\u00E9union',
                value: 'R\u00E9union',
                displayOrder: 187,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Saint Barth\u00E9lemy',
                value: 'Saint Barth\u00E9lemy',
                displayOrder: 188,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Saint Helena',
                value: 'Saint Helena',
                displayOrder: 189,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Saint Kitts and Nevis',
                value: 'Saint Kitts and Nevis',
                displayOrder: 190,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Saint Lucia',
                value: 'Saint Lucia',
                displayOrder: 191,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Saint Martin',
                value: 'Saint Martin',
                displayOrder: 192,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Saint Pierre and Miquelon',
                value: 'Saint Pierre and Miquelon',
                displayOrder: 193,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Saint Vincent and the Grenadines',
                value: 'Saint Vincent and the Grenadines',
                displayOrder: 194,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Samoa',
                value: 'Samoa',
                displayOrder: 195,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'San Marino',
                value: 'San Marino',
                displayOrder: 196,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Sao Tome and Principe',
                value: 'Sao Tome and Principe',
                displayOrder: 197,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Saudi Arabia',
                value: 'Saudi Arabia',
                displayOrder: 198,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Senegal',
                value: 'Senegal',
                displayOrder: 199,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Serbia',
                value: 'Serbia',
                displayOrder: 200,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Seychelles',
                value: 'Seychelles',
                displayOrder: 201,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Sierra Leone',
                value: 'Sierra Leone',
                displayOrder: 202,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Singapore',
                value: 'Singapore',
                displayOrder: 203,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Sint Maarten',
                value: 'Sint Maarten',
                displayOrder: 204,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Slovakia',
                value: 'Slovakia',
                displayOrder: 205,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Slovenia',
                value: 'Slovenia',
                displayOrder: 206,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Solomon Islands',
                value: 'Solomon Islands',
                displayOrder: 207,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Somalia',
                value: 'Somalia',
                displayOrder: 208,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'South Africa',
                value: 'South Africa',
                displayOrder: 209,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'South Georgia and the South Sandwich Islands',
                value: 'South Georgia and the South Sandwich Islands',
                displayOrder: 210,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'South Korea',
                value: 'South Korea',
                displayOrder: 211,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'South Sudan',
                value: 'South Sudan',
                displayOrder: 212,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Spain',
                value: 'Spain',
                displayOrder: 213,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Sri Lanka',
                value: 'Sri Lanka',
                displayOrder: 214,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Sudan',
                value: 'Sudan',
                displayOrder: 215,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Suriname',
                value: 'Suriname',
                displayOrder: 216,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Svalbard and Jan Mayen',
                value: 'Svalbard and Jan Mayen',
                displayOrder: 217,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Swaziland',
                value: 'Swaziland',
                displayOrder: 218,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Sweden',
                value: 'Sweden',
                displayOrder: 219,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Switzerland',
                value: 'Switzerland',
                displayOrder: 220,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Syria',
                value: 'Syria',
                displayOrder: 221,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Taiwan',
                value: 'Taiwan',
                displayOrder: 222,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Tajikistan',
                value: 'Tajikistan',
                displayOrder: 223,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Tanzania',
                value: 'Tanzania',
                displayOrder: 224,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Thailand',
                value: 'Thailand',
                displayOrder: 225,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Togo',
                value: 'Togo',
                displayOrder: 226,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Tokelau',
                value: 'Tokelau',
                displayOrder: 227,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Tonga',
                value: 'Tonga',
                displayOrder: 228,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Trinidad and Tobago',
                value: 'Trinidad and Tobago',
                displayOrder: 229,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Tunisia',
                value: 'Tunisia',
                displayOrder: 230,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Turkey',
                value: 'Turkey',
                displayOrder: 231,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Turkmenistan',
                value: 'Turkmenistan',
                displayOrder: 232,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Turks and Caicos Islands',
                value: 'Turks and Caicos Islands',
                displayOrder: 233,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Tuvalu',
                value: 'Tuvalu',
                displayOrder: 234,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'U.S. Virgin Islands',
                value: 'U.S. Virgin Islands',
                displayOrder: 235,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Uganda',
                value: 'Uganda',
                displayOrder: 236,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Ukraine',
                value: 'Ukraine',
                displayOrder: 237,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'United Arab Emirates',
                value: 'United Arab Emirates',
                displayOrder: 238,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'United Kingdom',
                value: 'United Kingdom',
                displayOrder: 239,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'United States',
                value: 'United States',
                displayOrder: 240,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'United States Minor Outlying Islands',
                value: 'United States Minor Outlying Islands',
                displayOrder: 241,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Uruguay',
                value: 'Uruguay',
                displayOrder: 242,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Uzbekistan',
                value: 'Uzbekistan',
                displayOrder: 243,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Vanuatu',
                value: 'Vanuatu',
                displayOrder: 244,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Vatican City',
                value: 'Vatican City',
                displayOrder: 245,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Venezuela',
                value: 'Venezuela',
                displayOrder: 246,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Vietnam',
                value: 'Vietnam',
                displayOrder: 247,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Wallis and Futuna',
                value: 'Wallis and Futuna',
                displayOrder: 248,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Western Sahara',
                value: 'Western Sahara',
                displayOrder: 249,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Yemen',
                value: 'Yemen',
                displayOrder: 250,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Zambia',
                value: 'Zambia',
                displayOrder: 251,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: 'Zimbabwe',
                value: 'Zimbabwe',
                displayOrder: 252,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
            {
                label: '\u00C5land Islands',
                value: '\u00C5land Islands',
                displayOrder: 253,
                doubleData: 0,
                hidden: false,
                description: '',
                readOnly: false,
            },
        ],
        productsInterestedIn: [
            {
                label: 'Style3D Studio',
                value: 'Style3D Studio',
            },
            {
                label: 'Style3D Fabric',
                value: 'Style3D Fabric',
            },
            {
                label: 'Style3D Cloud',
                value: 'Style3D Cloud',
            },
            {
                label: 'Style3D One',
                value: 'Style3D One',
            },
            {
                label: 'Style3D Atelier',
                value: 'Style3D Atelier',
            },
            {
                label: 'Style3D Simulator',
                value: 'Style3D Simulator',
            },
            {
                label: 'Other',
                value: 'Other',
            },
        ],
    };

    // 表单信息
    const formState = reactive<{
        labelCol: {
            span: number;
        };
        wrapperCol: {
            span: number;
        };
        formInline: {
            membership_type: string | null;
            phone: string;
            website: string;
            company_size: string | null;
            market_segment: string | null;
            job_title: string;
            form_source: string | null;
            firstname: string;
            lastname: string;
            email: string;
            country__region: string | null;
            other_message: string;
            products_interested_in: Array<string>;
        };
        policyConfirm: boolean;
        isFinished: boolean;
        formConfig: Array<{
            key: hsFormKey;
            dataType: string;
            inputType: string;
            name: string;
            label: string;
            tPlaceholder: string;
            business_user?: boolean;
            options?: Array<{
                label: string;
                value: string;
            }>;
        }>;
    }>({
        labelCol: { span: 9 },
        wrapperCol: { span: 16 },
        formInline: {
            membership_type: null,
            phone: '',
            website: '',
            company_size: null,
            market_segment: null,
            job_title: '',
            form_source: null,
            firstname: '',
            lastname: '',
            email: '',
            country__region: null,
            other_message: '',
            products_interested_in: [],
        },
        // companyTypeOption: [],
        policyConfirm: false,
        isFinished: false,
        // inputType 区分页面上输入框的样式
        formConfig: [
            {
                key: 'membership_type',
                dataType: 'string',
                inputType: 'selector',
                name: '用户类型',
                label: 'User Type',
                tPlaceholder: 'Please Select',
                options: createData.userTypeList,
            },
            {
                key: 'phone',
                dataType: 'tel',
                inputType: 'text',
                name: '办公电话号码',
                label: 'Work Phone',
                tPlaceholder: '+39 xxx',
                business_user: true,
            },
            {
                key: 'website',
                dataType: 'website',
                inputType: 'text',
                name: '网站',
                label: 'Website',
                tPlaceholder: '',
                business_user: true,
            },
            {
                key: 'company_size',
                dataType: 'text',
                inputType: 'selector',
                name: '公司规模',
                label: 'Company Size',
                tPlaceholder: 'Please Select',
                business_user: true,
                options: createData.companySizeList,
            },
            {
                key: 'market_segment',
                dataType: 'text',
                inputType: 'selector',
                name: '细分市场',
                label: 'Market Segment',
                tPlaceholder: 'Please Select',
                business_user: true,
                options: createData.marketSegmentList,
            },
            {
                key: 'job_title',
                dataType: 'text',
                inputType: 'text',
                name: '职位',
                label: 'Job title',
                tPlaceholder: '',
                business_user: true,
            },
            {
                key: 'form_source',
                dataType: 'text',
                inputType: 'selector',
                name: '申请试用的目的在于...',
                label: 'I would like to...',
                tPlaceholder: 'Please Select',
                options: createData.formSourceList,
            },
            {
                key: 'firstname',
                dataType: 'text',
                inputType: 'text',
                name: '姓',
                label: 'First Name',
                tPlaceholder: '',
            },
            {
                key: 'lastname',
                dataType: 'text',
                inputType: 'text',
                name: '名',
                label: 'Last Name',
                tPlaceholder: '',
            },
            {
                key: 'email',
                dataType: 'email',
                inputType: 'text',
                name: '邮箱',
                label: 'Business Email',
                tPlaceholder: 'Please use company email if you have',
            },
            {
                key: 'country__region',
                dataType: 'text',
                inputType: 'selector',
                name: '国家',
                label: 'Country/ Region',
                tPlaceholder: 'Please Select',
                options: createData.countryRegionList,
            },
            {
                key: 'products_interested_in',
                dataType: 'array',
                inputType: 'checkboxGroup',
                name: '感兴趣的产品',
                label: 'Products Interested In',
                tPlaceholder: 'Please Check',
                options: createData.productsInterestedIn,
            },
            {
                key: 'other_message',
                dataType: 'text',
                inputType: 'textarea',
                name: '其他信息',
                label: 'Leave your message',
                tPlaceholder: '',
            },
        ],
    });

    const isBusinessUser = () => {
        return formState.formInline.membership_type === 'Business Users';
    };

    // 根据是否是商业用户 显示与隐藏一些表单字段
    const filteredFormItems = computed(() => {
        if (!isBusinessUser()) {
            return formState.formConfig.filter((item) => {
                return !item.business_user;
            });
        } else {
            return formState.formConfig;
        }
    });

    const checkRequired = (key: hsFormKey) => {
        const nonBusinessFields = formState.formConfig
            .filter((item) => {
                return !item.business_user;
            })
            .map((item) => item.key);

        if (isBusinessUser() || (!isBusinessUser() && nonBusinessFields.indexOf(key) !== -1)) {
            // 商业用户 或 非商业用户且当前字段是非商业用户字段，进行必需性检验
            // console.log(key, rules[key]);
            return rules[key][0].required;
        } else {
            return false;
        }
    };

    // 校验规则，设置是否必需
    const rules: Record<string, Rule[]> = {
        membership_type: [{ required: true, type: 'string', trigger: 'blur' }],
        phone: [{ required: true, type: 'string', validator: validatePhone, trigger: ['blur'] }],
        website: [{ type: 'string', trigger: 'blur' }],
        company_size: [{ required: true, type: 'string', trigger: 'blur' }],
        market_segment: [{ required: true, type: 'string', trigger: 'blur' }],
        job_title: [{ required: true, type: 'string', trigger: 'blur' }],
        form_source: [{ required: true, type: 'string', trigger: 'blur' }],
        firstname: [{ required: true, type: 'string', trigger: 'blur' }],
        lastname: [{ required: true, type: 'string', trigger: 'blur' }],
        email: [{ required: true, type: 'email', validator: validateEmail, trigger: 'blur' }],
        country__region: [{ required: true, type: 'string', trigger: 'blur' }],
        other_message: [{ type: 'string' }],
        products_interested_in: [{ required: true, type: 'array', trigger: 'blur' }],
    };

    const genParams = () => {
        const params = Object.keys(formState.formInline).map((key) => {
            if (key !== 'products_interested_in')
                return {
                    name: key,
                    value: formState.formInline[key] || '',
                };
        });
        const productsListParams = formState.formInline['products_interested_in'].map((item) => {
            return {
                name: 'products_interested_in',
                value: item || '',
            };
        });
        return [...params, ...productsListParams].filter((item) => !!item);
    };

    // 表单提交回调
    const onSubmit = () => {
        (applyForm as Ref<FormInstance>).value.validate().then(async () => {
            if (isSubmitting.value) return;
            isSubmitting.value = true;

            postHubspotForm({
                submittedAt: new Date().getTime(),
                fields: genParams(),
                context: {
                    pageUri: window.location.href,
                },
            })
                .then((res) => {
                    submitSuccess();
                })
                .catch((err) => {
                    isSubmitting.value = false;
                    message.error(err._data.inlineMessage);
                });
        });
    };

    const { $bus } = useNuxtApp();
    const submitSuccess = () => {
        message.success(t('global_apply_trial_submit_success'));
        isSubmitting.value = false;
        applyForm.value?.resetFields();
        formState.isFinished = false;
        if (props.formType === 'mobile') {
            // 移动 端，关闭Header
            router.push('/thanks');
            $bus.$emit('close:mobileNav');
        } else {
            // PC 端，关闭弹窗
            router.push('/thanks');
            $bus.$emit('close:applyModal');
        }
    };

    const applyForm = ref<FormInstance | null>(null);

    const checkScrollSelector = (child: HTMLElement) => {
        // 获取所有下拉框
        const dropdown = document.querySelectorAll('.ant-select-dropdown');
        for (let i = 0; i < dropdown.length; i++) {
            const element = dropdown[i];
            // 如果下拉框包含子元素，则返回true
            if (element.contains(child)) return true;
        }
        // 否则返回false
        return false;
    };
    const addMouseWheelControl = () => {
        if (!matchMobile()) {
            const modalScroller = document.querySelector('.scroll-y-container') as HTMLElement;
            Observer.create({
                target: '.scroll-y-container', // can be any element (selector text is fine)
                type: 'wheel,touch', // comma-delimited list of what to listen for ("wheel,touch,scroll,pointer")
                onUp: (self) => {
                    const { event } = self;

                    if (!checkScrollSelector(event.target as HTMLElement)) modalScroller.scrollBy(0, -25);
                },
                onDown: (self) => {
                    const { event } = self;

                    if (!checkScrollSelector(event.target as HTMLElement)) modalScroller.scrollBy(0, 25);
                },
            });
        }
    };

    onBeforeMount(async () => {
        // 内嵌表单方式 https://knowledge.hubspot.com/forms/how-can-i-share-a-hubspot-form-if-im-using-an-external-site
        // initHubSpot().then((res) => {
        //     // console.log(res);
        //     const form = hbspt.forms.create({
        //         region: 'eu1',
        //         portalId: '25488444',
        //         formId: '7eb66cd8-db5a-4749-8415-86b620ed00d3',
        //         target: '#hubspotForm',
        //     });
        //     console.log('form', form);
        // });
        // const res = await Http.get('/account/apply/create');
        // formState.createData.tel_code = res.data.create_info.tel_code;
        // formState.createData.company_type = res.data.create_info.company_type;
    });
    onMounted(() => {
        // todo  待优化，防抖
        watch([() => formState.policyConfirm, formState.formInline], ([value0, value1]) => {
            // function check() {
            if (
                value0 === true &&
                Object.keys(value1).filter((key) => {
                    if (key === 'policyConfirm') return false;
                    return checkRequired(key) && !value1[key];
                }).length === 0
            )
                (applyForm.value as FormInstance)
                    .validate()
                    .then(() => {
                        formState.isFinished = true;
                    })
                    .catch((err) => {
                        formState.isFinished = false;
                    });
            else formState.isFinished = false;
            // }
            // debounce(check, 1000);
        });

        // 由于打开弹窗冻结页面滚动，需要重新添加滚动控制
        addMouseWheelControl();
    });
</script>
